import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import styles from './LandingPage4.module.css';

const APP_STORE_URL = 'https://apps.apple.com/us/app/autochef-meal-planning/id6758113114';

const LandingPage4 = () => {
  const [isVisible, setIsVisible] = useState({});
  const sectionsRef = useRef({});
  const [activeStep, setActiveStep] = useState(1);
  const timerRef = useRef(null);
  const [openFaq, setOpenFaq] = useState(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsVisible(prev => ({ ...prev, [entry.target.id]: true }));
          }
        });
      },
      { threshold: 0.1 }
    );

    Object.values(sectionsRef.current).forEach((ref) => {
      if (ref) observer.observe(ref);
    });

    return () => observer.disconnect();
  }, []);

  // Auto-advance steps every 10 seconds
  useEffect(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
    }

    timerRef.current = setInterval(() => {
      setActiveStep((prev) => (prev === 4 ? 1 : prev + 1));
    }, 10000);

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, [activeStep]);

  const handleStepClick = (step) => {
    setActiveStep(step);
  };

  const addToRefs = (id) => (el) => {
    if (el) sectionsRef.current[id] = el;
  };

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const testimonials = [
    {
      quote: "I used to spend Sunday nights stressed. Now I plan in 5 minutes.",
      name: "Sarah M.",
      context: "working mom",
      initials: "SM"
    },
    {
      quote: "Finally deleted my 47 recipe bookmarks. The grocery list feature alone is worth it.",
      name: "James K.",
      context: "",
      initials: "JK"
    },
    {
      quote: "Cut our food waste in half and grocery bill by 30%.",
      name: "Michelle T.",
      context: "family of 4",
      initials: "MT"
    }
  ];

  return (
    <div className={styles.container}>
      {/* Navigation */}
      <nav className={styles.nav}>
        <div className={styles.navContent}>
          <Link to="/" className={styles.logo}>
            <img src="/icons/autochef_logo.svg" alt="AutoChef" />
            <span>AutoChef</span>
          </Link>
          <div className={styles.navButtons}>
            <a href={APP_STORE_URL} target="_blank" rel="noopener noreferrer" className={styles.navButton}>Get the Free App</a>
            <Link to="/login" className={styles.navButton} style={{backgroundColor: '#fff', color: '#52cd92', border:'1px solid #52cd92'}}>Login</Link>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className={styles.hero}>
        <div className={styles.heroGrid}>
          {/* Left Column - Text Content */}
          <div className={styles.heroLeft}>
            <h1 className={styles.heroTitle}>
              Take meal planning off your plate.
            </h1>
            <p className={styles.heroSubtitle}>
              Take the stress out of the daily "what's for dinner?" question. 
              Save hours every week, reduce food waste, and spend less on groceries — all from your iPhone.
            </p>
            <div className={styles.heroButtons}>
              <a href={APP_STORE_URL} target="_blank" rel="noopener noreferrer">
                <button className={styles.heroCta}>
                  Download Free on iPhone <span className={styles.arrow}>→</span>
                </button>
              </a>
              <button 
                className={styles.secondaryCta}
                onClick={() => scrollToSection('how-it-works')}
              >
                How It Works
              </button>
            </div>
          </div>

          {/* Right Column - Phone Mockup */}
          <div className={styles.heroRight}>
            <img 
              src="images/AutoChefwithMealsSVG.png"
              alt="AutoChef App - This Week's Meals" 
              className={styles.mockupImage}
            />
          </div>
        </div>
      </section>

      {/* Social Proof / Testimonials Section */}
      <section 
        id="testimonials"
        ref={addToRefs('testimonials')}
        className={`${styles.socialProofSection} ${isVisible.testimonials ? styles.visible : ''}`}
      >
        <div className={styles.sectionContent}>
          <div className={styles.testimonialGrid}>
            {testimonials.map((testimonial, index) => (
              <div key={index} className={styles.testimonialCard}>
                <div className={styles.quoteIcon}>"</div>
                <p className={styles.testimonialText}>{testimonial.quote}</p>
                <div className={styles.testimonialAuthor}>
                  <div className={styles.testimonialAvatar}>{testimonial.initials}</div>
                  <div>
                    <div className={styles.authorName}>{testimonial.name}</div>
                    {testimonial.context && (
                      <div className={styles.authorRole}>{testimonial.context}</div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Key Benefits Section */}
      <section 
        id="benefits"
        ref={addToRefs('benefits')}
        className={`${styles.benefits} ${isVisible.benefits ? styles.visible : ''}`}
      >
        <div className={styles.sectionContent}>
          <div className={styles.benefitsHeader}>
            <h2 className={styles.benefitsTitle}>
              Plan a week of meals in under 2 minutes.
            </h2>
          </div>

          <div className={styles.benefitsGrid}>
            {/* Block 1 */}
            <div className={styles.benefitBlock}>
              <div className={styles.benefitIcon}>🍽️</div>
              <h3 className={styles.benefitTitle}>Never wonder what to cook again</h3>
              <p className={styles.benefitDescription}>
                Your nightly "What's for dinner?" crisis is officially retired. Set your 
                preferences (vegan, keto, gluten-free) and get meal plans that actually 
                match your life.
              </p>
            </div>

            {/* Block 2 */}
            <div className={styles.benefitBlock}>
              <div className={styles.benefitIcon}>📝</div>
              <h3 className={styles.benefitTitle}>Grocery lists that build themselves</h3>
              <p className={styles.benefitDescription}>
                Your grocery list updates itself every time you choose a recipe. No 
                more wandering the aisles trying to remember what you need—it's all 
                right there in your pocket.
              </p>
            </div>

            {/* Block 3 */}
            <div className={styles.benefitBlock}>
              <div className={styles.benefitIcon}>💰</div>
              <h3 className={styles.benefitTitle}>Reduce your food waste</h3>
              <p className={styles.benefitDescription}>
                AutoChef helps you use what you already have and avoid over-buying. 
                Your wilted spinach era is officially over.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section 
        id="how-it-works"
        ref={addToRefs('how-it-works')}
        className={`${styles.howItWorks} ${isVisible['how-it-works'] ? styles.visible : ''}`}
        style={{ backgroundColor: '#243029', color: '#FAFBFF' }}
      >
        <div className={styles.sectionContent}>
          <h2 className={styles.sectionTitle} style={{ color: '#FAFBFF' }}>How to AutoChef</h2>
          
          {/* Step Selector */}
          <div className={styles.stepSelector}>
            {[1, 2, 3, 4].map((step) => (
              <div key={step} className={styles.stepButtonWrapper}>
                {activeStep === step && (
                  <svg key={`progress-${activeStep}`} className={styles.progressRing} viewBox="0 0 64 64">
                    <circle
                      className={styles.progressRingCircle}
                      stroke="#FF6652"
                      strokeWidth="3"
                      fill="transparent"
                      r="29"
                      cx="32"
                      cy="32"
                    />
                  </svg>
                )}
                <button
                  className={`${styles.stepButton} ${activeStep === step ? styles.stepButtonActive : ''}`}
                  onClick={() => handleStepClick(step)}
                >
                  {step}
                </button>
              </div>
            ))}
          </div>

          {/* Active Step Content */}
          <div className={styles.stepContainer}>
            <div className={styles.stepRow}>
              <div className={styles.stepContent}>
                {activeStep === 1 && (
                  <>
                    <h3 className={styles.stepTitle} style={{ color: '#FAFBFF' }}>Set Your Preferences</h3>
                    <p className={styles.stepDescription} style={{ color: '#FAFBFF' }}>
                      Tell us about your dietary needs, cuisine preferences, allergens and family size. 
                      We'll use this to suggest meals you'll love.
                    </p>
                  </>
                )}
                {activeStep === 2 && (
                  <>
                    <h3 className={styles.stepTitle} style={{ color: '#FAFBFF' }}>Review Your Meals</h3>
                    <p className={styles.stepDescription} style={{ color: '#FAFBFF' }}>
                      Get personalized meal suggestions that use what you already have.
                      Add, change, or remove meals with a single tap.
                    </p>
                  </>
                )}
                {activeStep === 3 && (
                  <>
                    <h3 className={styles.stepTitle} style={{ color: '#FAFBFF' }}>Shop with Smart Lists</h3>
                    <p className={styles.stepDescription} style={{ color: '#FAFBFF' }}>
                      Dynamic grocery lists based on what you already have. Check off items as you shop with the mobile-friendly interface.
                    </p>
                  </>
                )}
                {activeStep === 4 && (
                  <>
                    <h3 className={styles.stepTitle} style={{ color: '#FAFBFF' }}>Cook & Enjoy</h3>
                    <p className={styles.stepDescription} style={{ color: '#FAFBFF' }}>
                      Easy to follow step-by-step instructions. Need to adjust the serving size last minute? No problem.
                    </p>
                  </>
                )}
              </div>
              
              <div className={styles.stepImage}>
                {activeStep === 1 && (
                  <img src="/images/SetYourPreferences.png" alt="Diet Preferences" className={styles.stepVideo} />
                )}
                {activeStep === 2 && (
                  <img src="/images/ReviewYourMeals1.png" alt="Meal Planning" className={styles.stepVideo} />
                )}
                {activeStep === 3 && (
                  <img src="/images/GroceryList1.png" alt="Grocery List" className={styles.stepVideo} />
                )}
                {activeStep === 4 && (
                  <img src="/images/SetYourPreferences1.png" alt="Recipe View" className={styles.stepVideo} />
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section 
        id="faq"
        ref={addToRefs('faq')}
        className={`${styles.faq} ${isVisible.faq ? styles.visible : ''}`}
      >
        <div className={styles.sectionContent}>
          <h2 className={styles.sectionTitle}>Questions? We've Got Answers.</h2>

          <div className={styles.faqList}>
            <div className={styles.faqItem}>
              <button 
                className={`${styles.faqQuestion} ${openFaq === 1 ? styles.faqQuestionOpen : ''}`}
                onClick={() => setOpenFaq(openFaq === 1 ? null : 1)}
              >
                <span>Do I really need another app?</span>
                <span className={styles.faqIcon}>{openFaq === 1 ? '−' : '+'}</span>
              </button>
              {openFaq === 1 && (
                <div className={styles.faqAnswer}>
                  <p>Fair question! But here's the thing: AutoChef isn't just another app. It's your personal meal planning assistant right on your iPhone that actually helps you stop wasting food, save money, and eat better. Plus, it doesn't judge your 3am leftover pizza decisions. Download it free and try it for 7 days — what's the worst that could happen? (Spoiler: You'll probably eat better.)</p>
                </div>
              )}
            </div>

            <div className={styles.faqItem}>
              <button 
                className={`${styles.faqQuestion} ${openFaq === 2 ? styles.faqQuestionOpen : ''}`}
                onClick={() => setOpenFaq(openFaq === 2 ? null : 2)}
              >
                <span>How much time will this actually save me?</span>
                <span className={styles.faqIcon}>{openFaq === 2 ? '−' : '+'}</span>
              </button>
              {openFaq === 2 && (
                <div className={styles.faqAnswer}>
                  <p>Most people save 3-5 hours per week. That's the time you'd normally spend wondering what to cook, making multiple grocery store trips, and searching for recipes. AutoChef does all of that in seconds.</p>
                </div>
              )}
            </div>

            <div className={styles.faqItem}>
              <button 
                className={`${styles.faqQuestion} ${openFaq === 4 ? styles.faqQuestionOpen : ''}`}
                onClick={() => setOpenFaq(openFaq === 4 ? null : 4)}
              >
                <span>What if I have dietary restrictions?</span>
                <span className={styles.faqIcon}>{openFaq === 4 ? '−' : '+'}</span>
              </button>
              {openFaq === 4 && (
                <div className={styles.faqAnswer}>
                  <p>You're covered! Vegan, vegetarian, pescatarian, gluten-free, dairy-free, nut allergies—whatever you need. Just set your restrictions during setup, and AutoChef will only suggest meals that work for you. No awkward "oops, that has dairy" moments.</p>
                </div>
              )}
            </div>

            <div className={styles.faqItem}>
              <button 
                className={`${styles.faqQuestion} ${openFaq === 5 ? styles.faqQuestionOpen : ''}`}
                onClick={() => setOpenFaq(openFaq === 5 ? null : 5)}
              >
                <span>How much does it cost?</span>
                <span className={styles.faqIcon}>{openFaq === 5 ? '−' : '+'}</span>
              </button>
              {openFaq === 5 && (
                <div className={styles.faqAnswer}>
                  <p>Just $3.99/month after your 7-day free trial. That's less than two fancy coffees, and you'll save way more than that on groceries every month. Download the app from the App Store to start your free trial — no credit card needed.</p>
                </div>
              )}
            </div>

            <div className={styles.faqItem}>
              <button 
                className={`${styles.faqQuestion} ${openFaq === 6 ? styles.faqQuestionOpen : ''}`}
                onClick={() => setOpenFaq(openFaq === 6 ? null : 6)}
              >
                <span>Can I cancel anytime?</span>
                <span className={styles.faqIcon}>{openFaq === 6 ? '−' : '+'}</span>
              </button>
              {openFaq === 6 && (
                <div className={styles.faqAnswer}>
                  <p>Yes! Cancel anytime, no questions asked. No hassle, no hidden fees, no "talk to our retention team" nonsense.</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className={styles.finalCta}>
        <div className={styles.finalCtaContent}>
          <h2 className={styles.finalCtaTitle}>
            Ready to put your meals on autopilot?
          </h2>
          
          <div className={styles.pricingBox}>
            <div className={styles.pricingHighlight}>Free for 7 Days</div>
            <div className={styles.pricingDetails}>Then just $3.99/month · Available on iPhone</div>
            <a href={APP_STORE_URL} target="_blank" rel="noopener noreferrer">
              <button className={styles.finalCtaButton}>Download on the App Store</button>
            </a>
            <div className={styles.pricingBenefits}>
              <span>✓ 7-day free trial</span>
              <span>✓ Cancel anytime</span>
              <span>✓ Full access to all features</span>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className={styles.footer}>
        <div className={styles.footerContent}>
          <div className={styles.footerBrand}>
            <img src="/icons/autochef_logo.svg" alt="AutoChef" />
            <span>AutoChef</span>
          </div>
          <div className={styles.footerLinks}>
            <Link to="/about">About</Link>
            <Link to="/help">Help</Link>
            <Link to="/privacy-policy">Privacy</Link>
            <Link to="/terms">Terms</Link>
          </div>
          <div className={styles.footerCopy}>
            © {new Date().getFullYear()} AutoChef. All rights reserved.
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage4;
